import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import Strings from '../Strings';
import { escapeHtml } from '../utils';
import { DATE_MONTH_DAY_YEAR } from '../constants';
import { getProgramDisplayName } from '../utils/cmsPrograms';
import { openModalAction } from '../actions/modal';
import { hasPermission, PERMISSIONS } from '../utils/userPermissions';
import { isShipper, isSuperUser } from '../utils/userRoles';

const isUnfinished = status => !['delivered', 'cancelled'].includes(status);

export function shouldShowEndedEnrollmentShippingWarning({ order, shipment }) {
  const resource = shipment || order;
  const enrollments = shipment
    ? shipment.enrollment_details?.filter(enrollment => enrollment.status === 'ended')
    : order?.ended_enrollments;
  const hasUnfinishedFulfillment = isUnfinished(resource?.status)
    || (!shipment && (order?.has_active_shipments || order?.shipments?.some(item => isUnfinished(item.status))));
  return resource?.flow === 'outbound' && hasUnfinishedFulfillment && !!enrollments?.length;
}

export function EndedEnrollmentShippingWarning({ order, shipment, compact = false }) {
  if (!shouldShowEndedEnrollmentShippingWarning({ order, shipment })) {
    return null;
  }
  const resource = shipment || order;
  const enrollments = shipment
    ? shipment.enrollment_details.filter(enrollment => enrollment.status === 'ended')
    : order.ended_enrollments;
  const details = [
    Strings.shipping.endedEnrollmentWarning,
    ...(resource.patient_uuid ? [Strings.formatString(Strings.shipping.endedEnrollmentPatient, resource.patient_uuid)] : []),
    Strings.formatString(Strings.shipping.endedEnrollments, enrollments.map(enrollment => (
      Strings.formatString(
        Strings.shipping.endedEnrollmentSummary,
        getProgramDisplayName(enrollment.program, enrollment.sub_program),
        enrollment.terminated_at ? moment(enrollment.terminated_at).format(DATE_MONTH_DAY_YEAR) : '-',
      )
    )).join(', ')),
  ];
  if (compact) {
    const tooltip = details.join('\n');
    return (
      <div
        className="signal-alert"
        data-testid="ended-enrollment-shipping-warning"
        data-tooltip-id="tooltip"
        data-tooltip-html={escapeHtml(tooltip).replaceAll('\n', '<br />')}
        data-tooltip-place="bottom"
        style={{ marginLeft: 6, cursor: 'help', flexShrink: 0 }}
        role="img"
        aria-label={Strings.shipping.endedEnrollmentWarning}
        tabIndex={0}
        onClick={event => event.stopPropagation()}
      />
    );
  }
  return (
    <div role="alert" className="color-brand-red" data-testid="ended-enrollment-shipping-warning">
      {details.map((detail, index) => <div key={index}>{detail}</div>)}
    </div>
  );
}

EndedEnrollmentShippingWarning.propTypes = {
  compact: PropTypes.bool,
  order: PropTypes.object,
  shipment: PropTypes.object,
};

function EnrollmentShippingWarning({ patient, enrollmentIds, showShipmentDetails }) {
  const orders = new Map();
  enrollmentIds.forEach(id => {
    patient?.patientEnrollment?.enrollments?.[id]?.orders?.forEach(order => {
      if (order.flow === 'outbound' && (isUnfinished(order.status) || order.shipments?.some(shipment => isUnfinished(shipment.status)))) {
        orders.set(order.order_uuid, order);
      }
    });
  });
  if (!orders.size) {
    return null;
  }
  const shipments = new Map();
  orders.forEach(order => {
    order.shipments?.forEach(shipment => {
      shipments.set(shipment.shipment_uuid, { shipment, orderId: order.order_uuid });
    });
  });
  const unrestricted = isShipper() || isSuperUser();
  const canViewShipments = unrestricted || hasPermission(PERMISSIONS.SHIPPING_SHIPMENTS_VIEW);
  return (
    <div role="alert" className="double-width-left" data-testid="termination-shipping-warning">
      <div>{Strings.shipping.terminationShippingWarning}</div>
      <ul>
        <li>
          {Strings.shipping.terminationOrders}
          {': '}
          {[...orders.values()].map((order, index) => {
            const canViewOrder = unrestricted || hasPermission(PERMISSIONS.SHIPPING_ORDERS_VIEW)
              || (['pending_review', 'rejected_by_doctor'].includes(order.status) && hasPermission(PERMISSIONS.SHIPPING_PENDING_REVIEW_ORDERS_VIEW));
            return (
              <React.Fragment key={order.order_uuid}>
                {index > 0 && ', '}
                {canViewOrder
                  ? <Link to={`/shipping/order-details/${order.order_uuid}`} target="_blank" rel="noopener noreferrer">{order.order_uuid}</Link>
                  : order.order_uuid}
                {' ('}
                {Strings.shipping.orderStatuses[order.status] || order.status}
                )
              </React.Fragment>
            );
          })}
        </li>
        {!!shipments.size && (
          <li>
            {Strings.shipping.terminationShipments}
            {': '}
            {[...shipments.values()].map(({ shipment, orderId }, index) => (
              <React.Fragment key={shipment.shipment_uuid}>
                {index > 0 && ', '}
                {canViewShipments
                  ? <button type="button" className="link" onClick={() => showShipmentDetails(shipment.shipment_uuid, orderId)}>{shipment.shipment_uuid}</button>
                  : shipment.shipment_uuid}
                {' ('}
                {Strings.shipping.shipmentStatuses[shipment.status] || shipment.status}
                )
              </React.Fragment>
            ))}
          </li>
        )}
      </ul>
    </div>
  );
}

EnrollmentShippingWarning.propTypes = {
  patient: PropTypes.object,
  enrollmentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  showShipmentDetails: PropTypes.func.isRequired,
};

export default connect(null, dispatch => ({
  showShipmentDetails: (shipmentId, orderId) => dispatch(openModalAction('shipment-details', { shipmentId, orderId })),
}))(EnrollmentShippingWarning);
